import {
  externalObjectProperties,
  externalObjects,
  analyticsCharts,
  selectDataTypes,
  multiSelectDataTypes,
} from "@/src/constants/analyticConstants.js";
import { aggregationOperators } from "@/src/constants/formulaConstants.js";
import {
  validateFormulaStructure,
  makeCompositeKey,
  buildPropertyIdMigrationMap,
  migrateChartConfig,
  migrateFilters,
  migrateCalculatedFieldFormulas,
} from "@/src/utils/analyticsChartUtils.js";

const excludedPropertyTypes = ["multiselect", "multi_select", "tags"];

function buildConfigSnapshot(state) {
  return JSON.stringify({
    selectedChartType: state.selectedChartType,
    chartConfiguration: state.chartConfiguration,
    selectedObjects: {
      primaryId: state.selectedObjects.primary?.id || null,
      secondaryIds: (state.selectedObjects.secondary || []).map((o) => o.id),
    },
    filters: state.filters,
    calculatedFields: state.calculatedFields,
    sqlQuery: state.sqlQuery,
  });
}

const state = {
  selectedChartType: null,
  chartConfiguration: {},
  selectedObjects: {
    primary: null,
    secondary: [],
  },
  availableProperties: [],
  filters: {
    groups: [],
  },
  sqlQuery: "",
  configurationExists: false,
  crmObjects: [],
  chartData: {},
  objectsMetadata: [],
  renderedChartType: null,
  renderedChartConfiguration: {},
  selectedFormId: null,
  formSchema: null,
  calculatedFields: [],
  hasUnsavedChanges: false,
  chartHasBeenCreated: false,
  configurationTotal: 0,
  savedConfigSnapshot: null,
};

function transformFormAttributesToProperties(
  schema,
  formId,
  formName,
  hasSecondaryObjects = false,
) {
  if (!schema?.data?.attributes) return [];

  const properties = [];

  const processAttribute = (attr) => {
    if (attr.type === "section") {
      if (attr.config?.children && Array.isArray(attr.config.children)) {
        attr.config.children.forEach((child) => processAttribute(child));
      }
      return;
    }

    if (attr.type === "image") {
      return;
    }

    const originalName = attr.label || attr.name;

    const propertyName = hasSecondaryObjects
      ? `Form.${originalName}`
      : originalName;

    properties.push({
      id: hasSecondaryObjects
        ? makeCompositeKey("forms", attr.id)
        : attr.id,
      rawId: attr.id,
      slug: attr.config?.attributeData?.slug || attr.fieldName || attr.id,
      name: propertyName,
      type: attr.config?.attributeData?.type || attr.type,
      dataType: attr.config?.attributeData?.dataType || attr.type,
      objectId: `forms_${formId}`,
      objectSlug: "forms",
      objectName: formName,
      originalName: originalName,
      options: (attr.options || []).map((opt) => ({
        id: opt.value,
        name: opt.label || opt.value,
      })),
    });
  };

  schema.data.attributes.forEach((attr) => processAttribute(attr));

  return properties;
}

const mutations = {
  SET_SELECTED_CHART_TYPE(state, chartType) {
    state.selectedChartType = chartType;
  },
  SET_CHART_CONFIGURATION(state, config) {
    state.chartConfiguration = config;
  },
  UPDATE_CHART_CONFIGURATION_FIELD(state, { field, value }) {
    state.chartConfiguration = {
      ...state.chartConfiguration,
      [field]: value,
    };
  },
  SET_SELECTED_OBJECTS(state, { primary, secondary }) {
    state.selectedObjects = {
      primary: primary || null,
      secondary: secondary || [],
    };
  },
  SET_AVAILABLE_PROPERTIES(state, properties) {
    state.availableProperties = properties;
  },
  SET_FILTERS(state, filters) {
    if (!filters || !filters.groups) {
      state.filters = { groups: [] };
    } else {
      state.filters = filters;
    }
  },
  ADD_FILTER_GROUP(state, group) {
    if (!state.filters) {
      state.filters = { groups: [] };
    }
    if (!state.filters.groups) {
      state.filters.groups = [];
    }
    state.filters.groups.push(group);
  },
  UPDATE_FILTER_GROUP(state, { groupIndex, group }) {
    if (groupIndex >= 0 && groupIndex < state.filters.groups.length) {
      state.filters.groups[groupIndex] = {
        ...state.filters.groups[groupIndex],
        ...group,
      };
    }
  },
  REMOVE_FILTER_GROUP(state, groupIndex) {
    if (groupIndex >= 0 && groupIndex < state.filters.groups.length) {
      state.filters.groups.splice(groupIndex, 1);
    }
  },
  ADD_CONDITION_TO_GROUP(state, { groupIndex, condition }) {
    if (groupIndex >= 0 && groupIndex < state.filters.groups.length) {
      state.filters.groups[groupIndex].conditions.push(condition);
    }
  },
  UPDATE_CONDITION_IN_GROUP(state, { groupIndex, conditionIndex, condition }) {
    if (
      groupIndex >= 0 &&
      groupIndex < state.filters.groups.length &&
      conditionIndex >= 0 &&
      conditionIndex < state.filters.groups[groupIndex].conditions.length
    ) {
      state.filters.groups[groupIndex].conditions[conditionIndex] = {
        ...state.filters.groups[groupIndex].conditions[conditionIndex],
        ...condition,
      };
    }
  },
  REMOVE_CONDITION_FROM_GROUP(state, { groupIndex, conditionIndex }) {
    if (
      groupIndex >= 0 &&
      groupIndex < state.filters.groups.length &&
      conditionIndex >= 0 &&
      conditionIndex < state.filters.groups[groupIndex].conditions.length
    ) {
      state.filters.groups[groupIndex].conditions.splice(conditionIndex, 1);
    }
  },
  CLEAR_FILTERS(state) {
    state.filters = {
      groups: [],
    };
  },
  SET_SQL_QUERY(state, query) {
    state.sqlQuery = query;
  },
  SET_CONFIGURATION_EXISTS(state, exists) {
    state.configurationExists = exists;
  },
  SET_CRM_OBJECTS(state, objects) {
    state.crmObjects = objects;
  },
  SET_CHART_DATA(state, data) {
    state.chartData = data !== undefined ? data : {};
    state.renderedChartConfiguration = state.chartConfiguration
      ? JSON.parse(JSON.stringify(state.chartConfiguration))
      : {};
  },
  SET_OBJECTS_METADATA(state, metadata) {
    state.objectsMetadata = metadata;
  },
  SET_RENDERED_CHART_TYPE(state, chartType) {
    state.renderedChartType = chartType;
  },
  SET_RENDERED_CHART_CONFIGURATION(state, config) {
    state.renderedChartConfiguration = config ? JSON.parse(JSON.stringify(config)) : {};
  },
  SET_SELECTED_FORM(state, { formId, schema }) {
    state.selectedFormId = formId;

    if (schema?.data?.attributes) {
      const externalProps =
        externalObjectProperties[externalObjects.FORMS] || [];

      state.formSchema = {
        ...schema,
        data: {
          ...schema.data,
          attributes: [...externalProps, ...schema.data.attributes],
        },
      };
    } else {
      state.formSchema = schema;
    }
  },
  CLEAR_FORM_DATA(state) {
    state.selectedFormId = null;
    state.formSchema = null;
  },
  SET_CALCULATED_FIELDS(state, fields) {
    state.calculatedFields = fields;
  },
  SET_HAS_UNSAVED_CHANGES(state, value) {
    state.hasUnsavedChanges = value;
  },
  SET_SAVED_CONFIG_SNAPSHOT(state, snapshot) {
    state.savedConfigSnapshot = snapshot;
  },
  SET_CHART_HAS_BEEN_CREATED(state, value) {
    state.chartHasBeenCreated = value;
  },
  SET_CONFIGURATION_TOTAL(state, total) {
    state.configurationTotal = total || 0;
  },
  APPLY_CONFIGURATION_BATCH(state, payload) {
    Object.keys(payload).forEach((key) => {
      if (!(key in state)) return;
      if (key === "filters") {
        state.filters = payload.filters?.groups ? payload.filters : { groups: [] };
      } else {
        state[key] = payload[key];
      }
    });
  },
  RESET_CONFIGURATION(state) {
    state.selectedChartType = null;
    state.chartConfiguration = {};
    state.selectedObjects = {
      primary: null,
      secondary: [],
    };
    state.availableProperties = [];
    state.filters = {
      groups: [],
    };
    state.sqlQuery = "";
    state.configurationExists = false;
    state.crmObjects = [];
    state.chartData = {};
    state.objectsMetadata = [];
    state.renderedChartType = null;
    state.renderedChartConfiguration = {};
    state.selectedFormId = null;
    state.formSchema = null;
    state.calculatedFields = [];
    state.hasUnsavedChanges = false;
    state.chartHasBeenCreated = false;
    state.configurationTotal = 0;
    state.savedConfigSnapshot = null;
  },
  RESET_SIDE_PANEL_SELECTIONS(state) {
    state.selectedChartType = null;
    state.chartConfiguration = {};
    state.filters = { groups: [] };
    state.calculatedFields = [];
  },
};

const actions = {
  setSelectedChartType({ commit }, chartType) {
    commit("SET_SELECTED_CHART_TYPE", chartType);
    if (chartType === analyticsCharts.FUNNEL) {
      commit("SET_CALCULATED_FIELDS", []);
    }
  },
  setChartConfiguration({ commit }, config) {
    commit("SET_CHART_CONFIGURATION", config);
  },
  updateChartConfigurationField({ commit }, { field, value }) {
    commit("UPDATE_CHART_CONFIGURATION_FIELD", { field, value });
  },
  async updateSelectedObjects({ dispatch, commit, state }, { primary, secondary, skipReset = false }) {
    const currentPrimary = state.selectedObjects?.primary;
    const currentSecondary = state.selectedObjects?.secondary || [];
    let newSecondary = secondary || [];

    if (!skipReset) {
      const isPrimaryChanged = currentPrimary?.id && primary?.id !== currentPrimary?.id;

      if (isPrimaryChanged) {
        commit("RESET_SIDE_PANEL_SELECTIONS");
        newSecondary = [];
      } else {
        const currentSecondaryIds = new Set(
          currentSecondary.filter(Boolean).map((obj) => obj?.id)
        );
        const hasNewSecondary = newSecondary
          .filter(Boolean)
          .some((obj) => obj?.id && !currentSecondaryIds.has(obj.id));

        if (hasNewSecondary) {
          commit("RESET_SIDE_PANEL_SELECTIONS");
        }
      }
    }

    commit("SET_SELECTED_OBJECTS", { primary, secondary: newSecondary });

    const objects = [primary, ...newSecondary].filter(Boolean);

    if (objects.length > 0) {
      await Promise.all(
        objects.map((obj) =>
          dispatch("analytics/fetchObjectSchema", obj, { root: true }),
        ),
      );

      await dispatch("buildAvailableProperties");
    }
  },
  async buildAvailableProperties({ state, commit, dispatch, rootGetters }) {
    const { selectedObjects, formSchema, selectedFormId } = state;

    const objectsFromStore = rootGetters["objects/objects"] || [];
    let properties = [];

    if (
      selectedObjects.primary?.slug === "forms" &&
      formSchema &&
      selectedFormId
    ) {
      const formName = formSchema.data?.name || "Form";
      const hasSecondaryObjects = selectedObjects.secondary.length > 0;
      const formProperties = transformFormAttributesToProperties(
        formSchema,
        selectedFormId,
        formName,
        hasSecondaryObjects,
      );
      properties = [...formProperties];

      if (selectedObjects.secondary.length === 0) {
        commit("SET_AVAILABLE_PROPERTIES", properties);
        dispatch("setObjectsMetadata");
        return;
      }
    }

    const allObjects =
      selectedObjects.primary?.slug === "forms"
        ? [...selectedObjects.secondary]
        : [selectedObjects.primary, ...selectedObjects.secondary].filter(
            Boolean,
          );

    const objectsWithSchemas = allObjects
      .map((selectedObj) => {
        return objectsFromStore.find(
          (obj) => obj.id === selectedObj.id || obj.slug === selectedObj.slug,
        );
      })
      .filter(Boolean);

    const multipleObjects =
      selectedObjects.primary?.slug === "forms"
        ? selectedObjects.secondary.length > 0
        : objectsWithSchemas.length > 1;

    const isDealObject = (slug) => ["deals", "deal"].includes(slug);
    const hasDealObject = objectsWithSchemas.some((obj) =>
      isDealObject(obj.slug),
    );

    let pipelineOptions = [];
    let pipelineStageOptions = [];
    let sortedStageNames = [];

    if (hasDealObject) {
      try {
        const [pipelinesResponse, stagesResponse] = await Promise.all([
          dispatch("pipelines/fetchPipelines", {}, { root: true }),
          dispatch("pipelines/fetchPipelineStages", {}, { root: true }),
        ]);

        if (pipelinesResponse?.success && pipelinesResponse?.pipelines) {
          pipelineOptions = pipelinesResponse.pipelines.map((p) => ({
            id: p.id,
            name: p.name,
          }));
        }

        if (stagesResponse?.success && stagesResponse?.pipeline_stages) {
          const pipelineIndexMap = new Map(
            (pipelinesResponse?.pipelines || []).map((p, i) => [p.id, i])
          );
          const sortedStages = [...stagesResponse.pipeline_stages].sort((a, b) => {
            const piA = pipelineIndexMap.has(a.pipeline_id) ? pipelineIndexMap.get(a.pipeline_id) : Infinity;
            const piB = pipelineIndexMap.has(b.pipeline_id) ? pipelineIndexMap.get(b.pipeline_id) : Infinity;
            if (piA !== piB) return piA - piB;
            return (a.order_index ?? 0) - (b.order_index ?? 0);
          });
          pipelineStageOptions = sortedStages.map((s) => ({ id: s.id, name: s.name }));
          const seen = new Set();
          sortedStageNames = sortedStages.map((s) => s.name).filter((n) => {
            if (seen.has(n)) return false;
            seen.add(n);
            return true;
          });
        }
      } catch (error) {
        console.error("Error fetching pipeline options:", error);
      }
    }

    objectsWithSchemas.forEach((obj) => {
      const objectName = obj.name;

      if (obj.properties) {
        obj.properties.forEach((prop) => {
          const propertyName = multipleObjects
            ? `${objectName}.${prop.name}`
            : prop.name;

          let options = prop.options || [];

          if (isDealObject(obj.slug)) {
            if (prop.slug === "pipeline_id") {
              options = pipelineOptions;
            } else if (prop.slug === "pipeline_stage_id") {
              options = pipelineStageOptions;
            }
          }

          const customSortOptions = (() => {
            const propType = (prop.type || prop.dataType || "").toLowerCase();
            const isOptionType = selectDataTypes.includes(propType) || multiSelectDataTypes.includes(propType);
            if (!isOptionType) return null;
            if (isDealObject(obj.slug) && prop.slug === "deal_stage") return sortedStageNames;
            const opts = options.length > 0 ? options : (prop.options || []);
            if (opts.length > 0) return opts.map((o) => o.name || o.label || o.value || String(o)).filter(Boolean);
            return null;
          })();

          properties.push({
            id: multipleObjects
              ? makeCompositeKey(obj.slug, prop.id)
              : prop.id,
            rawId: prop.id,
            slug: prop.slug,
            name: propertyName,
            type: prop.type,
            dataType: prop.dataType || prop.type,
            objectId: obj.id,
            objectSlug: obj.slug,
            objectName: objectName,
            originalName: prop.name,
            options: options,
            customSortType: customSortOptions?.length > 0 ? "business_order" : null,
            customSortOptions: customSortOptions?.length > 0 ? customSortOptions : null,
          });
        });
      }
    });

    const migrationMap = buildPropertyIdMigrationMap(
      state.availableProperties,
      properties,
    );

    commit("SET_AVAILABLE_PROPERTIES", properties);

    if (Object.keys(migrationMap).length > 0) {
      commit(
        "SET_CHART_CONFIGURATION",
        migrateChartConfig(state.chartConfiguration, migrationMap),
      );
      commit("SET_FILTERS", migrateFilters(state.filters, migrationMap));
      commit(
        "SET_CALCULATED_FIELDS",
        migrateCalculatedFieldFormulas(state.calculatedFields, migrationMap),
      );
    }

    dispatch("setObjectsMetadata");
  },
  addFilterGroup({ commit }, group) {
    commit("ADD_FILTER_GROUP", group);
  },
  updateFilterGroup({ commit }, { groupIndex, group }) {
    commit("UPDATE_FILTER_GROUP", { groupIndex, group });
  },
  removeFilterGroup({ commit }, groupIndex) {
    commit("REMOVE_FILTER_GROUP", groupIndex);
  },
  addConditionToGroup({ commit }, { groupIndex, condition }) {
    commit("ADD_CONDITION_TO_GROUP", { groupIndex, condition });
  },
  updateConditionInGroup(
    { commit },
    { groupIndex, conditionIndex, condition },
  ) {
    commit("UPDATE_CONDITION_IN_GROUP", {
      groupIndex,
      conditionIndex,
      condition,
    });
  },
  removeConditionFromGroup({ commit }, { groupIndex, conditionIndex }) {
    commit("REMOVE_CONDITION_FROM_GROUP", { groupIndex, conditionIndex });
  },
  setFilters({ commit }, filters) {
    commit("SET_FILTERS", filters);
  },
  clearFilters({ commit }) {
    commit("CLEAR_FILTERS");
  },
  setSqlQuery({ commit }, query) {
    commit("SET_SQL_QUERY", query);
  },
  setConfigurationExists({ commit }, exists) {
    commit("SET_CONFIGURATION_EXISTS", exists);
  },
  resetConfiguration({ commit }) {
    commit("RESET_CONFIGURATION");
  },
  clearRenderedOutput({ commit }) {
    commit("SET_CHART_DATA", {});
    commit("SET_RENDERED_CHART_TYPE", null);
    commit("SET_RENDERED_CHART_CONFIGURATION", {});
    commit("SET_CHART_HAS_BEEN_CREATED", false);
    commit("SET_HAS_UNSAVED_CHANGES", false);
  },
  async resetChartConfiguration({ commit, dispatch }) {
    commit("SET_SELECTED_CHART_TYPE", null);
    commit("SET_FILTERS", { groups: [] });
    commit("SET_CALCULATED_FIELDS", []);
    commit("SET_CHART_CONFIGURATION", {});
    await dispatch("clearRenderedOutput");
  },
  setCrmObjects({ commit }, objects) {
    commit("SET_CRM_OBJECTS", objects);
  },
  setChartData({ commit }, data) {
    commit("SET_CHART_DATA", data);
  },
  setConfigurationTotal({ commit }, total) {
    commit("SET_CONFIGURATION_TOTAL", total);
  },
  setObjectsMetadata({ commit, state, rootGetters }) {
    const { selectedObjects, formSchema, selectedFormId } = state;

    if (
      selectedObjects.primary?.slug === "forms" &&
      formSchema &&
      selectedFormId
    ) {
      const formName = formSchema.data?.name || "Form";
      const hasSecondaryObjects = selectedObjects.secondary.length > 0;
      const formProperties = transformFormAttributesToProperties(
        formSchema,
        selectedFormId,
        formName,
        hasSecondaryObjects,
      );
      const metadata = [
        {
          id: `forms_${selectedFormId}`,
          slug: "forms",
          name: hasSecondaryObjects ? "Form" : formName,
          attributes: formProperties,
        },
      ];
      commit("SET_OBJECTS_METADATA", metadata);
      return;
    }

    const objectsFromStore = rootGetters["objects/objects"] || [];
    const metadata = [];
    const allObjects = [
      selectedObjects.primary,
      ...selectedObjects.secondary,
    ].filter(Boolean);

    allObjects.forEach((selectedObj) => {
      const obj = objectsFromStore.find(
        (o) => o.id === selectedObj.id || o.slug === selectedObj.slug,
      );

      if (obj) {
        metadata.push({
          id: obj.id,
          slug: obj.slug,
          name: obj.name,
          attributes: obj.properties || [],
        });
      }
    });

    commit("SET_OBJECTS_METADATA", metadata);
  },
  setRenderedChartType({ commit }, chartType) {
    commit("SET_RENDERED_CHART_TYPE", chartType);
  },
  setRenderedChartConfiguration({ commit }, config) {
    commit("SET_RENDERED_CHART_CONFIGURATION", config);
  },
  setSelectedForm({ commit }, { formId, schema }) {
    commit("SET_SELECTED_FORM", { formId, schema });
  },
  clearFormData({ commit }) {
    commit("CLEAR_FORM_DATA");
  },
  setCalculatedFields({ commit }, fields) {
    commit("SET_CALCULATED_FIELDS", fields);
  },
  setHasUnsavedChanges({ commit }, value) {
    commit("SET_HAS_UNSAVED_CHANGES", value);
  },
  snapshotSavedConfig({ commit, state }) {
    commit("SET_SAVED_CONFIG_SNAPSHOT", buildConfigSnapshot(state));
  },
  setChartHasBeenCreated({ commit }, value) {
    commit("SET_CHART_HAS_BEEN_CREATED", value);
  },
  applyConfigurationBatch({ commit }, payload) {
    commit("APPLY_CONFIGURATION_BATCH", payload);
  },
};

const measureDataTypes = ["number", "integer", "decimal", "float", "currency"];

const getters = {
  selectedChartType: (state) => state.selectedChartType,
  chartConfiguration: (state) => state.chartConfiguration,
  selectedObjects: (state) => state.selectedObjects,
  availableProperties: (state, getters) => {
    const brokenFieldsSet = new Set(
      getters.brokenCalculatedFields.map((bf) => `${bf.name}_${bf.formula}`),
    );

    const calcFieldsAsProperties = (state.calculatedFields || [])
      .filter((field) => {
        if (!field.name || !field.formula) return false;
        const fieldKey = `${field.name}_${field.formula}`;
        return !brokenFieldsSet.has(fieldKey);
      })
      .map((field, index) => ({
        id: `calc_field_${index}`,
        rawId: `calc_field_${index}`,
        slug: `calc_field_${index}`,
        name: field.name,
        type: "number",
        dataType: "number",
        objectId: "calculated",
        objectSlug: "calculated",
        objectName: "Calculated Fields",
        originalName: field.name,
        isCalculatedField: true,
        formula: field.formula,
        options: [],
      }));

    const filteredProperties = state.availableProperties.filter((prop) => {
      if (!prop || typeof prop !== "object") return true;
      const dataType = (prop.dataType || prop.type || "").toLowerCase();
      return !excludedPropertyTypes.some((excluded) =>
        dataType.includes(excluded)
      );
    });

    return [...filteredProperties, ...calcFieldsAsProperties];
  },
  filters: (state) => state.filters,
  filterGroups: (state) => state.filters.groups || [],
  sqlQuery: (state) => state.sqlQuery,
  configurationExists: (state) => state.configurationExists,
  crmObjects: (state) => state.crmObjects,
  chartData: (state) => state.chartData,
  objectsMetadata: (state) => state.objectsMetadata,
  renderedChartType: (state) => state.renderedChartType,
  renderedChartConfiguration: (state) => state.renderedChartConfiguration,
  selectedFormId: (state) => state.selectedFormId,
  formSchema: (state) => state.formSchema,
  isFormObject: (state) =>
    state.selectedObjects.primary?.slug === "forms" &&
    state.selectedFormId !== null,
  hasChartData: (state) => {
    const data = state.chartData;
    if (!data) return false;
    return (
      data.categories?.length > 0 ||
      data.series?.length > 0 ||
      data.labels?.length > 0 ||
      data.kpis?.length > 0 ||
      data.rows?.length > 0 ||
      data.stages?.length > 0
    );
  },
  primaryObjectID: (state) => state.selectedObjects.primary?.id || null,
  secondaryObjectIDs: (state) =>
    state.selectedObjects.secondary?.map((obj) => obj.id) || [],
  calculatedFields: (state) => state.calculatedFields,
  noChartDataAvailable: (state) => state.chartData === null,
  hasUnsavedChanges: (state) => state.hasUnsavedChanges,
  hasConfigChanged: (state) => buildConfigSnapshot(state) !== state.savedConfigSnapshot,
  chartHasBeenCreated: (state) => state.chartHasBeenCreated,
  configurationTotal: (state) => state.configurationTotal,
  numberProperties: (state, getters) => {
    return getters.availableProperties.filter((prop) => {
      if (!prop || typeof prop !== "object") return false;
      const dataType = (prop.dataType || prop.type || "").toLowerCase();
      return measureDataTypes.some((type) => dataType.includes(type));
    });
  },
  allNumberProperties: (state, getters) => {
    return getters.numberProperties || [];
  },
  allFilterableProperties: (state, getters) => {
    return getters.availableProperties.filter((prop) => {
      if (!prop || typeof prop !== "object") return false;
      if (prop.isCalculatedField) return false;
      return true;
    });
  },
  brokenCalculatedFields: (state) => {
    const calcFields = state.calculatedFields || [];
    const validPropertyIds = new Set(
      state.availableProperties.map((p) => p.id),
    );
    // Also accept raw IDs for backward compat with existing formulas
    state.availableProperties.forEach((p) => {
      if (p.rawId && p.rawId !== p.id) validPropertyIds.add(p.rawId);
    });

    const chartType = state.selectedChartType;
    const supportsAggregation =
      chartType === analyticsCharts.TABLE ||
      chartType === analyticsCharts.PIVOT_TABLE;

    return calcFields
      .map((field, index) => ({ ...field, index }))
      .filter((field) => {
        if (!field.formula) return false;

        const propertyRefs = field.formula.match(/\{([^}]+)\}/g) || [];
        const referencedIds = propertyRefs.map((ref) => ref.slice(1, -1));
        const hasInvalidRefs = referencedIds.some(
          (id) => !validPropertyIds.has(id),
        );

        if (hasInvalidRefs) return true;

        if (!supportsAggregation) {
          const formulaLower = field.formula.toLowerCase();
          const hasAggregation = aggregationOperators.some((op) => {
            const pattern = new RegExp(`\\b${op}\\s*\\(`, "i");
            return pattern.test(formulaLower);
          });

          if (hasAggregation) return true;
        }

        if (!validateFormulaStructure(field.formula)) return true;

        return false;
      });
  },
  unrelatedSecondaryObjects: (state, getters, rootState, rootGetters) => {
    const { selectedObjects } = state;

    if (
      selectedObjects.primary?.slug !== "forms" ||
      selectedObjects.secondary.length === 0
    ) {
      return [];
    }

    const objectsFromStore = rootGetters["objects/objects"] || [];
    const unrelated = [];

    selectedObjects.secondary.forEach((secObj) => {
      const secObjFull = objectsFromStore.find((o) => o.id === secObj.id);
      if (!secObjFull || !secObjFull.relationships) {
        unrelated.push(secObj);
        return;
      }

      const hasRelationship = selectedObjects.secondary.some((otherSecObj) => {
        if (otherSecObj.id === secObj.id) return false;

        return secObjFull.relationships.some(
          (rel) =>
            rel.source_object_id === otherSecObj.id ||
            rel.target_object_id === otherSecObj.id ||
            rel.source_object_id === otherSecObj.slug ||
            rel.target_object_id === otherSecObj.slug,
        );
      });

      if (!hasRelationship && selectedObjects.secondary.length > 1) {
        unrelated.push(secObj);
      }
    });

    return unrelated;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
